import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Share2, ShieldCheck } from 'lucide-react';
import { useDeleteSharedLinkMutation, useSharedLinksQuery } from '../api/useReportsQueries';
import { CreateLinkModal } from '../components/CreateLinkModal';
import { DeleteLinkModal } from '../components/DeleteLinkModal';
import { SharedLinkRow } from '../components/SharedLinkRow';

export function ReportsPage() {
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteShareId, setDeleteShareId] = useState<string | null>(null);

  const listQ = useSharedLinksQuery();
  const deleteMut = useDeleteSharedLinkMutation();

  const links = listQ.data?.data ?? [];

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-5">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="text-left">
          <p className="m-0 text-[11px] font-semibold tracking-[0.12em] text-[var(--dash-brand)] uppercase">
            Reports
          </p>
          <h1 className="m-0 mt-1 text-2xl font-semibold tracking-tight text-[var(--dash-ink)]">
            Shared Dashboards
          </h1>
          <p className="mt-1 mb-0 text-sm text-[var(--dash-muted)]">
            {listQ.isPending
              ? 'Loading shared links…'
              : `${links.length} shared link${links.length === 1 ? '' : 's'} · grant external campaign access`}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="dash-btn-gradient inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition"
        >
          <Share2 className="h-4 w-4" />
          Share Dashboard
        </button>
      </header>

      {listQ.isPending ? (
        <div className="flex justify-center py-24">
          <Loader2 className="h-6 w-6 animate-spin text-[var(--dash-brand)]" />
        </div>
      ) : links.length === 0 ? (
        <div className="flex min-h-[42vh] flex-col items-center justify-center rounded-3xl border border-dashed border-[var(--dash-line)] bg-white px-6 py-12 text-center">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--dash-brand-soft)] text-[var(--dash-brand)]">
            <ShieldCheck className="h-7 w-7" />
          </div>
          <h2 className="m-0 text-lg font-semibold text-[var(--dash-ink)]">
            No shared links present
          </h2>
          <p className="mt-2 mb-6 max-w-sm text-sm text-[var(--dash-muted)]">
            Generate a shareable link via the &lsquo;Share Dashboard&rsquo; button to grant
            external access.
          </p>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="dash-btn-gradient inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-sm font-semibold text-white"
          >
            <Share2 className="h-4 w-4" />
            Share Dashboard
          </button>
        </div>
      ) : (
        <div className="overflow-hidden rounded-3xl border border-[var(--dash-line)] bg-white shadow-[0_10px_28px_-22px_rgba(16,38,73,0.35)]">
          <div className="flex flex-col divide-y divide-[var(--dash-line)]">
            {links.map((item, index) => (
              <SharedLinkRow
                key={item.shareId || index}
                item={item}
                index={index}
                onView={() => item.token && navigate(`/report-detail/${item.token}`)}
                onDelete={() => item.shareId && setDeleteShareId(item.shareId)}
              />
            ))}
          </div>
        </div>
      )}

      <CreateLinkModal open={createOpen} onClose={() => setCreateOpen(false)} />

      <DeleteLinkModal
        open={Boolean(deleteShareId)}
        isDeleting={deleteMut.isPending}
        onCancel={() => setDeleteShareId(null)}
        onConfirm={() => {
          if (!deleteShareId) return;
          deleteMut.mutate(deleteShareId, { onSuccess: () => setDeleteShareId(null) });
        }}
      />
    </div>
  );
}
